import { PropTypes } from 'prop-types';
import React, { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAnalyticsEnabled,
  selectPermissions,
  selectRole,
  selectSite,
  selectUserGroup,
  selectUserName,
} from '../../store/authReducer';
import { selectModal } from '../../store/modal';
import { logout } from '../../views/login/actions';
import { ModalRoot } from './ModalRoot';
import DetergentModalBridge from '../../views/settings/components/ManageDetergents/DetergentModalBridge';
import Navbar from './Nav';
import SideNav from './SideNav';
import { viewPermissionRefs, findPermissionRef } from './UserGroupPermissions';
import { getDetergentPods } from '../../views/detergentsTemp/actions';
import { selectDetergentPods } from '../../views/detergentsTemp/reducer';

const MOBILE_WIDTH = 700;

const getSideMenuUrl = (baseUrl, relativeUrl) => {
  const fullUrl = `${baseUrl}/${relativeUrl}`;
  return baseUrl ? fullUrl : null;
};

// Base menu items configuration - created once to avoid recreation
// URLs are generated dynamically based on baseUrl
const BASE_MENU_ITEMS_CONFIG = [
  {
    route: 'overview',
    icon: 'uil-estate',
    label: 'overview',
    permissionRef: viewPermissionRefs.site,
  },
  {
    route: 'tasks',
    icon: 'uil-file-check-alt',
    label: 'tasks',
    permissionRef: viewPermissionRefs.task,
  },
  {
    route: 'maintenance',
    icon: 'uil-wrench',
    label: 'maintenance',
    permissionRef: viewPermissionRefs.view_maintenance_page,
  },
  {
    route: 'detergents',
    icon: 'uil-water-glass',
    label: 'detergents',
    permissionRef: viewPermissionRefs.detergent,
  },
  {
    route: 'washcounts',
    icon: 'uil-chart-pie',
    label: 'washCounts',
    permissionRef: viewPermissionRefs.wash_count,
  },
  {
    route: 'claims',
    icon: 'uil-exclamation-triangle',
    label: 'claims',
    permissionRef: viewPermissionRefs.warranty,
  },
  {
    route: 'rewashes',
    icon: 'uil-raindrops-alt',
    label: 'rewashes',
    permissionRef: viewPermissionRefs.rewash,
  },
  {
    route: 'purchases',
    icon: 'uil-usd-square',
    label: 'purchases',
    permissionRef: viewPermissionRefs.expense,
  },
  {
    route: 'utilities',
    icon: 'uil-dashboard',
    label: 'utilities',
    permissionRef: viewPermissionRefs.expense,
  },
  {
    route: 'inventory',
    icon: 'uil-archive-alt',
    label: 'inventory',
    permissionRef: viewPermissionRefs.inventory,
  },
  {
    route: 'analytics',
    icon: 'uil-chart',
    label: 'analytics',
    permissionRef: viewPermissionRefs.analytics,
  },
  {
    route: 'settings',
    icon: 'uil-cog',
    label: 'settings',
    permissionRef: viewPermissionRefs.settings,
  },
];

export const getSideMenuItems = (baseUrl, hasDetergentPods = false) => {
  // Map config to menu items with URLs
  const items = BASE_MENU_ITEMS_CONFIG.map((config) => ({
    to: getSideMenuUrl(baseUrl, config.route),
    icon: config.icon,
    label: config.label,
    permissionRef: config.permissionRef,
    requiresPods: config.requiresPods || false,
  }));

  // Filter out detergentsTemp item if it requires pods but none exist
  return items.filter((item) => !(item.requiresPods && !hasDetergentPods));
};

// NOTE: could load theme from context,
const Main = ({ children }) => {
  const dispatch = useDispatch();
  const [isNavOpen, setNavState] = React.useState(window.innerWidth >= MOBILE_WIDTH);

  // Select state values from Redux store
  const selectedSite = useSelector(selectSite);
  const siteId = selectedSite ? selectedSite.toString() : null;
  const modal = useSelector(selectModal);
  const username = useSelector(selectUserName);
  const analyticsToggle = useSelector(selectAnalyticsEnabled);
  const permissions = useSelector(selectPermissions);
  const userGroup = useSelector(selectUserGroup);
  const detergentPods = useSelector(selectDetergentPods);

  // Create action dispatchers
  const handleLogout = useCallback(() => dispatch(logout()), [dispatch]);

  // Memoize hasDetergentPods calculation
  const hasDetergentPods = useMemo(
    () => Boolean(detergentPods && detergentPods.length > 0),
    [detergentPods]
  );

  // Memoize base URL to avoid string concatenation on every render
  const baseUrl = useMemo(() => `/${siteId}`, [siteId]);

  // Memoize side menu items to avoid recreation on every render
  const sideMenuItems = useMemo(
    () => getSideMenuItems(baseUrl, hasDetergentPods),
    [baseUrl, hasDetergentPods]
  );

  return (
    <>
      <div className="app-main">
        <SideNav
          homeUrl="/"
          menuItems={sideMenuItems}
          username={username}
          analyticsEnabled={analyticsToggle}
          permissions={permissions}
          setNavState={setNavState}
          isOpen={isNavOpen}
          userGroup={userGroup}
          mobileBreakpoint={MOBILE_WIDTH}
          logout={handleLogout}
        />
        <main>
          <Navbar
            username={username}
            siteId={siteId}
            setNavState={setNavState}
            isNavOpen={isNavOpen}
          />
          <div className="page-container">{children}</div>
        </main>
      </div>
      <DetergentModalBridge />
      <ModalRoot {...modal} />
    </>
  );
};

Main.propTypes = {
  children: PropTypes.node,
};

export default Main;
